import clsx from "clsx";
import { AvatarSize, IntegrationAvatar } from "../../../shared/MergeAvatars";
import { LogoSampleEntry } from "../utils/logoSeed";

// How many times a column's logos repeat before the loop point. A single pass rarely adds up to
// a full screen's height, which left a visible dead gap at the bottom of short columns.
const REPEATS_PER_HALF = 2;

const LogoColumn = ({
  integrations,
  reverse,
}: {
  integrations: LogoSampleEntry[];
  reverse: boolean;
}) => {
  // Doubled so the track can loop seamlessly: translating the first half fully offscreen lines
  // up exactly with the second half taking its place. Each half already repeats the list a few
  // times, so the loop point is invisible either way.
  const half = Array.from({ length: REPEATS_PER_HALF }, () => integrations).flat();
  const doubled = [...half, ...half];

  return (
    <div className="w-24 overflow-hidden">
      <div
        className={clsx(
          "flex h-max flex-col items-center gap-8 py-8 [will-change:transform]",
          reverse ? "animate-marquee-vertical-reverse" : "animate-marquee-vertical"
        )}
      >
        {doubled.map((integration, index) => (
          <IntegrationAvatar
            key={`${integration.id}-${index}`}
            integrationName={integration.name}
            squareImage={integration.square_image}
            size={AvatarSize.xl}
          />
        ))}
      </div>
    </div>
  );
};

type Props = {
  columns: LogoSampleEntry[][];
  side: "left" | "right";
};

const IntegrationsLogoRail = ({ columns, side }: Props) => {
  const nonEmptyColumns = columns.filter((column) => column.length > 0);
  if (!nonEmptyColumns.length) return null;

  return (
    <div
      aria-hidden
      className={clsx(
        // Fixed to the viewport rather than absolute within the page, so it always spans the
        // full screen height regardless of the top nav bar's height or how tall the page content
        // ends up -- and stays put rather than scrolling away if the page grows past one screen.
        "pointer-events-none fixed inset-y-0 z-0 hidden gap-2 overflow-hidden opacity-20 grayscale lg:flex lg:flex-row",
        side === "left" ? "left-0" : "right-0"
      )}
    >
      {/* Alternating direction per column, so adjacent columns don't drift in lockstep. */}
      {nonEmptyColumns.map((column, index) => (
        <LogoColumn key={index} integrations={column} reverse={index % 2 === 1} />
      ))}
    </div>
  );
};

export default IntegrationsLogoRail;
